// Refonte du suivi — écran du suivi (lot 4).
//
// Monté à la place de l'étape « Suivi » de la fiche, seulement pour une
// cliente dont l'identifiant figure dans le drapeau suivi_v2 (app_config).
// Le drapeau est vide : rien ne change pour Anissa avant la bascule.
//
// Un seul écran, trois états :
//   * un brouillon est ouvert (nouveau ou repris de l'ancien écran) → Modifier ;
//   * une version est validée mais pas encore sortie → on le dit ;
//   * sinon → Préparer la version suivante.

import { useCallback, useEffect, useMemo, useState } from 'react';
import Modifier from './Modifier';
import Preparer from './Preparer';
import Historique from './Historique';
import DepuisDernierRdv from './DepuisDernierRdv';
import ARegarderEnPremier from './ARegarderEnPremier';
import useRessenti from './services/useRessenti';
import ValiderPublier from './ValiderPublier';
import { lireValidation } from './services/suiviPublication';
import { drapeauConnu, lireDrapeauAvecDelai } from './services/suiviDrapeau';
import {
  noteDemande, resumeDerniereVersion,
} from './suiviEcran';
import {
  abandonnerBrouillon,
  brouillonOuvert,
  lireVersion,
  lireVersions,
  nomVersion,
  preparerBrouillon,
} from './services/suiviVersions';

export default function SuiviPage({
  client,
  db,
  versionsInitiales = null,
  brouillonInitial = null,
  sourceInitiale = null,
  onDirtyChange,
}) {
  const options = useMemo(() => (db ? { db } : {}), [db]);
  const [versions, setVersions] = useState(versionsInitiales);
  const [brouillon, setBrouillon] = useState(brouillonInitial);
  const [source, setSource] = useState(sourceInitiale);
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [aValider, setAValider] = useState(null);
  const [validationEnAttente, setValidationEnAttente] = useState(null);
  const [demandeEnAttente, setDemandeEnAttente] = useState(null);
  const [aReessayer, setAReessayer] = useState(null);
  const clientId = client?.id;
  // Lot 6 : ressentis et repas depuis le dernier rendez-vous (jamais bloquant).
  const ressenti = useRessenti(client);

  const charger = useCallback(async () => {
    setErreur(null);
    setAReessayer(null);
    try {
      const liste = await lireVersions(clientId, options);
      setVersions(liste);
      setAValider(null);
      const derniereVersion = liste[liste.length - 1];
      setValidationEnAttente(derniereVersion?.etat === 'validee' && derniereVersion.suivi_validation_id
        ? await lireValidation(derniereVersion.suivi_validation_id, options)
        : null);
      const ouvert = brouillonOuvert(liste);
      if (ouvert) {
        setBrouillon(await lireVersion(ouvert.consultation_id, options));
        setSource(null);
      } else {
        setBrouillon(null);
        const derniere = liste[liste.length - 1];
        setSource(derniere ? await lireVersion(derniere.consultation_id, options) : null);
      }
    } catch (error) {
      setErreur(error?.message || 'Chargement impossible.');
    }
  }, [clientId, options]);

  useEffect(() => {
    if (versionsInitiales) return undefined;
    let actif = true;
    Promise.resolve().then(() => { if (actif) charger(); });
    return () => { actif = false; };
  }, [charger, versionsInitiales]);

  // Le texte « à sa demande » part par l'enregistrement automatique du
  // brouillon (nouveau ou déjà ouvert) : visible, réessayé, jamais perdu.
  function ouvrirAvecDemande(ligne, demande) {
    const notes = ligne ? noteDemande(ligne.private_notes, demande) : null;
    setDemandeEnAttente(notes ? { consultationId: ligne.id, changes: { private_notes: notes } } : null);
    setBrouillon(ligne);
  }

  async function preparer(choix) {
    const { motif, rdvOrdinal, demande } = choix;
    setOccupe(true);
    setErreur(null);
    setAReessayer(null);
    try {
      const ligne = await preparerBrouillon({ clientId, motif, rdvOrdinal }, options);
      ouvrirAvecDemande(ligne, demande);
      setVersions(await lireVersions(clientId, options));
    } catch (error) {
      if (error?.nature === 'brouillon_existant' && error.detail) {
        ouvrirAvecDemande(await lireVersion(error.detail, options).catch(() => null), demande);
        setVersions(await lireVersions(clientId, options).catch(() => versions));
      } else {
        setErreur(error?.message || 'Préparation impossible.');
        setAReessayer(() => () => preparer(choix));
      }
    } finally {
      setOccupe(false);
    }
  }

  async function abandonner(ligne) {
    await abandonnerBrouillon(ligne, options);
    setDemandeEnAttente(null);
    setBrouillon(null);
    await charger();
  }

  const relire = () => (brouillon ? lireVersion(brouillon.id, options) : Promise.resolve(null));
  const liste = versions || [];
  const derniere = liste[liste.length - 1] || null;
  const enAttenteDeSortie = derniere?.etat === 'validee';
  const prochaine = `V${(derniere?.rang || 0) + (brouillon ? 0 : 1)}`;
  const resume = resumeDerniereVersion(liste);
  const versionBrouillon = brouillon ? liste.find((v) => v.consultation_id === brouillon.id) : null;

  return (
    <div className="jrn-stage suivi-page">
      <header className="jrn-block__head">
        <h2 className="jrn-block__title">Suivi de {client?.prenom || 'la cliente'}</h2>
        {resume && <span className="jrn-meta-chip">{resume}</span>}
      </header>

      {erreur && (
        <p className="jrn-error" role="alert">
          {erreur}{' '}
          <button type="button" className="jrn-btn jrn-btn--ghost" onClick={() => (aReessayer ? aReessayer() : charger())}>Réessayer</button>
        </p>
      )}

      {versions === null && !erreur && <p className="jrn-block__intro">Chargement…</p>}

      {versions !== null && brouillon && aValider && (
        <ValiderPublier
          key={"valider-" + aValider.id}
          client={client}
          ligne={aValider}
          db={db}
          nomVersion={versionBrouillon ? nomVersion(versionBrouillon) : prochaine}
          onRetour={(ligneCourante) => { setBrouillon(ligneCourante); setAValider(null); }}
          onTermine={() => charger()}
        />
      )}

      {versions !== null && brouillon && !aValider && (
        <Modifier
          key={brouillon.id}
          brouillon={brouillon}
          client={client}
          nomVersion={versionBrouillon ? nomVersion(versionBrouillon) : prochaine}
          repris={!brouillon.suivi_motif}
          onRecharger={relire}
          onAbandonner={abandonner}
          onValider={(ligne) => { setDemandeEnAttente(null); setAValider(ligne); }}
          onDirtyChange={onDirtyChange}
          ressenti={ressenti}
          changementsInitiaux={demandeEnAttente?.consultationId === brouillon.id ? demandeEnAttente.changes : null}
        />
      )}

      {versions !== null && !brouillon && enAttenteDeSortie && validationEnAttente && (
        <ValiderPublier
          key={"sortie-" + validationEnAttente.id}
          client={client}
          validationInitiale={validationEnAttente}
          db={db}
          nomVersion={nomVersion(derniere)}
          onTermine={() => charger()}
        />
      )}

      {versions !== null && !brouillon && enAttenteDeSortie && !validationEnAttente && (
        <section className="jrn-block">
          <p className="jrn-block__intro">
            {nomVersion(derniere)} est validée, pas encore publiée.
          </p>
        </section>
      )}

      {versions !== null && !brouillon && !enAttenteDeSortie && (
        <Preparer
          client={client}
          protocolPhases={source?.protocol_phases || null}
          prochaineVersion={prochaine}
          onPreparer={preparer}
          occupe={occupe}
          aRegarder={<ARegarderEnPremier donnees={ressenti.donnees} depuis={ressenti.depuis} />}
        />
      )}

      {versions !== null && (brouillon || enAttenteDeSortie) && !aValider && (
        <ARegarderEnPremier donnees={ressenti.donnees} depuis={ressenti.depuis} />
      )}

      {versions !== null && !aValider && (
        <DepuisDernierRdv client={client} rdv={ressenti.rdv} donnees={ressenti.donnees} />
      )}

      <Historique client={client} versions={liste} db={db} />
    </div>
  );
}

/**
 * Aiguillage de l'étape « Suivi » : nouvel écran si la cliente est dans le
 * drapeau suivi_v2, sinon l'écran actuel, inchangé. Tant que le drapeau n'est
 * pas lu, un simple « Chargement… » (au plus 2 s) ; illisible ou trop lent =
 * « non » (écran actuel). Le drapeau est gardé pour la session.
 */
export function SuiviAiguillage({ client, ancienEcran, db, onDirtyChange }) {
  const clientId = client?.id || null;
  const [actif, setActif] = useState(() => (drapeauConnu(clientId) !== undefined
    ? { id: clientId, valeur: drapeauConnu(clientId) }
    : { id: null, valeur: false }));

  // La décision est prise une fois par visite de la fiche : un drapeau lu
  // plus tard (après un délai dépassé) ne change jamais l'écran en cours.
  const decide = actif.id === clientId;
  useEffect(() => {
    if (!clientId || decide) return undefined;
    let vivant = true;
    lireDrapeauAvecDelai(clientId, { db }).then((valeur) => {
      if (vivant) setActif((precedent) => (precedent.id === clientId ? precedent : { id: clientId, valeur }));
    });
    return () => { vivant = false; };
  }, [clientId, db, decide]);

  if (!clientId) return ancienEcran;
  const valeur = decide ? actif.valeur : null;
  if (valeur === null) return <p className="jrn-block__intro">Chargement…</p>;
  if (valeur) return <SuiviPage client={client} db={db} onDirtyChange={onDirtyChange} />;
  return ancienEcran;
}
